/**
 * Banner de SORTEIO ativo no app ("compre X e concorra a Y").
 *
 * Aparece pra TODO visitante quando a loja tem campanha ativa com "exibir no
 * app". Clique abre a folha com prêmio, como participar, data do sorteio,
 * produtos participantes e — se o cliente estiver identificado — as milhares
 * dele. Registra VISTO (1x por sessão/6h) e CLIQUE pra medir o retorno.
 *
 * Root próprio (#raffle-banner-root) — mesma regra do frete grátis: NÃO
 * renderizar dentro do HeaderIsland (quebrava os modais).
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { customerPhone, fmtLocal, track, slug, useMyRaffles } from './_raffleShared';
import { t, tr, useI18n } from '../i18n';

interface RaffleApp {
  id: number; name: string; prize: string; description: string | null; banner_image_url: string | null;
  product_names: string[]; ticket_per_unit: boolean; min_order_value: number;
  draw_at_local: string | null; ends_at_local: string | null; tickets_issued: number;
}
export function RaffleBanner() {
  // assina a troca de idioma (ver i18n.ts)
  useI18n();
  const [raffles, setRaffles] = useState<RaffleApp[]>([]);
  const [aberto, setAberto] = useState<RaffleApp | null>(null);
  useEffect(() => {
    if (!slug()) return;
    fetch(`/api/delivery/raffles/active?store_slug=${encodeURIComponent(slug())}`)
      .then((r) => r.json()).then((j: { raffles?: RaffleApp[] }) => {
        const rs = j.raffles || []; setRaffles(rs); rs.forEach((r) => track(r.id, 'view'));
      }).catch(() => setRaffles([]));
  }, []);
  if (raffles.length === 0) return null;
  const r = raffles[0];
  return (
    <>
      <button
        type="button"
        onClick={() => { track(r.id, 'click'); setAberto(r); }}
        className="w-full text-left bg-gradient-to-r from-pink-600 to-rose-500 text-white px-4 py-3 flex items-center gap-3 shadow-md active:opacity-90"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0"><i className="fas fa-gift text-lg" /></div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-extrabold leading-tight truncate">Concorra a {r.prize}</p>
          <p className="text-[11px] opacity-90 leading-tight truncate">
            {r.product_names.length ? `Peça ${r.product_names.slice(0, 2).join(' ou ')} e ganhe sua milhar` : t('Peça o produto participante e ganhe sua milhar')}
            {r.draw_at_local ? ` · sorteio ${fmtLocal(r.draw_at_local).slice(0, 5)}` : ''}
          </p>
        </div>
        <i className="fas fa-chevron-right opacity-80" />
      </button>
      {aberto && <RaffleSheet r={aberto} onClose={() => setAberto(null)} />}
    </>
  );
}

function RaffleSheet({ r, onClose }: { r: RaffleApp; onClose: () => void }) {
  const { my, loading } = useMyRaffles();
  const meus = my.find((m) => m.id === r.id);
  const phone = customerPhone();
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/60" onClick={onClose} role="dialog" aria-modal="true">
      <div className="w-full sm:w-[480px] bg-white rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {r.banner_image_url ? <img src={r.banner_image_url} alt="" className="w-full h-40 object-cover sm:rounded-t-2xl" /> : (
          <div className="h-24 bg-gradient-to-r from-pink-600 to-rose-500 flex items-center justify-center sm:rounded-t-2xl"><i className="fas fa-gift text-4xl text-white/90" /></div>
        )}
        <div className="p-5 flex flex-col gap-4">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-pink-600">{t('Sorteio')}</p>
            <h2 className="text-xl font-extrabold text-gray-900 leading-tight">{r.prize}</h2>
            <p className="text-sm text-gray-500">{tr(r.name)}</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Info icone="fa-pizza-slice" rotulo="Participa quem pede" valor={r.product_names.length ? r.product_names.join(', ') : t('produtos da campanha')} />
            <Info icone="fa-calendar-check" rotulo="Sorteio" valor={r.draw_at_local ? fmtLocal(r.draw_at_local) : t('data a anunciar')} />
            <Info icone="fa-ticket" rotulo={t("Como ganha a milhar")} valor={r.ticket_per_unit ? t('1 milhar por unidade pedida') : t('1 milhar por pedido')} />
            <Info icone="fa-users" rotulo={t("Milhares já distribuídas")} valor={String(r.tickets_issued)} />
            <div className="col-span-2 text-[11px] text-gray-500">{t('Vale para pedidos feitos aqui no app ou direto na loja. Pedidos por iFood e outros aplicativos parceiros não participam.')}</div>
          </div>
          {r.description ? <p className="text-sm text-gray-700 whitespace-pre-line bg-gray-50 border border-gray-200 rounded-xl p-3">{tr(r.description)}</p> : null}
          <div className="border-t border-gray-100 pt-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">{t('Minhas milhares')}</p>
            {!phone ? (
              <p className="text-sm text-gray-600">Identifique-se com seu telefone (em "Minha conta") pra ver suas milhares aqui.</p>
            ) : loading ? (
              <p className="text-sm text-gray-500"><i className="fas fa-circle-notch fa-spin mr-1" />{t('Carregando…')}</p>
            ) : !meus || meus.numbers.length === 0 ? (
              <p className="text-sm text-gray-600">Você ainda não tem milhar nesta campanha. Peça {r.product_names[0] ? <b>{r.product_names[0]}</b> : t('o produto participante')} e ela aparece aqui e no seu cupom.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {meus.numbers.map((n, i) => (
                  <span key={i} className={`font-mono font-black text-lg px-3 py-1.5 rounded-lg border ${n.status === 'valid' ? 'bg-pink-50 border-pink-200 text-pink-700' : 'bg-gray-100 border-gray-200 text-gray-400 line-through'}`} title={`pedido #${n.order_code || ''}`}>{n.milhar}</span>
                ))}
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} className="w-full h-11 rounded-xl bg-gray-900 text-white font-bold text-sm">{t('Fechar')}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Info({ icone, rotulo, valor }: { icone: string; rotulo: string; valor: string }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-2.5">
      <p className="text-[10px] font-bold uppercase text-gray-500"><i className={`fas ${icone} mr-1 text-pink-500`} />{rotulo}</p>
      <p className="text-[13px] font-semibold text-gray-900 leading-snug">{valor}</p>
    </div>
  );
}
